import { QueryTable } from "@/components/query-table/QueryTable";
import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import { MonthYearTableFilter } from "@/components/data-table/components/MonthYearTableFilter";
import { Module, PATH_CONTEST_AGREEMENT } from "@/constants";
import {
  contestAgreementList,
  contestAgreementListRestricted,
} from "@/schemas/contestAgreement";
import type { ContestAgreementRead } from "@/types/contestAgreement";
import { fetchContestAgreementsList } from "@/services/contestAgreements";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { SuccessFetchListResponse } from "@/services/api";
import { useCallback, useMemo } from "react";
import { generatePath } from "react-router-dom";
import { useContestAgreementsFieldPermissions } from "../hooks/useContestAgreementsFieldPermissions";
import { useContestAgreementsColumnOverrides } from "../hooks/useContestAgreementsColumnOverrides";

const ContestAgreementsList = () => {
  const options = useCreateDataTableOptions<ContestAgreementRead>(
    Module.ContestAgreements,
  );

  const queryKey = useDynamicQueryParams(Module.ContestAgreements, options);
  const { canSeePresentationImageCount } =
    useContestAgreementsFieldPermissions();

  const getNextPageParam = useCallback(
    (lastPage: SuccessFetchListResponse<ContestAgreementRead>) =>
      lastPage.last_item,
    [],
  );

  const fetchList = useCallback(
    async ({ pageParam }: { pageParam?: string }) =>
      fetchContestAgreementsList({
        lastItem: pageParam,
        filters: options.filters,
        sorting: options.sorting[0],
      }),
    [options.filters, options.sorting],
  );

  const query = useInfiniteQuery({
    queryKey,
    queryFn: fetchList,
    initialPageParam: undefined,
    getNextPageParam,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const queryValue = useMemo(() => ({ query }), [query]);

  const columnOverrides = useContestAgreementsColumnOverrides();

  return (
    <DataTableQueryContextProvider value={queryValue}>
      <QueryTable<ContestAgreementRead>
        schema={
          canSeePresentationImageCount
            ? contestAgreementList
            : contestAgreementListRestricted
        }
        name={Module.ContestAgreements}
        translationKey="contestAgreements"
        query={query}
        options={options}
        tableFeatures={{
          exportSettings: { moduleName: Module.ContestAgreements },
          initialColumnVisibility: {
            agreement2_presentation_image_count: canSeePresentationImageCount,
          },
        }}
        pathBuilder={(row) =>
          generatePath(PATH_CONTEST_AGREEMENT, { id: row.id })
        }
        columnOverrides={columnOverrides}
      >
        <QueryTable.Header breadcrumbLabel="contestAgreements.title.multiple" />
        <div className="flex flex-row gap-2 justify-between items-center py-2">
          <QueryTable.Menubar className="mb-0 grow">
            <QueryTable.Menubar.Tools />
            <QueryTable.Menubar.Columns />
            <QueryTable.Menubar.Settings />
          </QueryTable.Menubar>

          <MonthYearTableFilter
            columnId="period"
            className="shrink-0 grow-0"
            popoverClassName="z-100"
            disabled={query.isFetching}
          />
        </div>
        <QueryTable.Content>
          <QueryTable.Status />
        </QueryTable.Content>
      </QueryTable>
    </DataTableQueryContextProvider>
  );
};

export default ContestAgreementsList;
